import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  signal,
  Type,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EMPTY_STRING, ICON_URLS } from '@app/constants';
import { ModalHeaderActionsConfig, ModalService } from '@app/services/modal.service';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-modal-atom',
  templateUrl: './modal-atom.component.html',
  styleUrls: ['./modal-atom.component.scss'],
})
export class ModalAtomComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('content', { read: ViewContainerRef, static: true })
  content!: ViewContainerRef;
  @ViewChild('headerActions', { read: ViewContainerRef, static: false })
  headerActionsContainer!: ViewContainerRef;
  title = EMPTY_STRING;
  isBold = false;
  isCentered = false;
  showSeparator = false;
  isOpen = false;
  showCloseButton = true;
  isRightAligned = false;
  headerActionsConfig: ModalHeaderActionsConfig | null = null;
  subscription = new Subscription();
  closeIcon = ICON_URLS.Close;
  @Input() className: string = 'modal__dialog';

  // Signal to track when view is initialized
  private readonly viewInitialized = signal(false);
  // Signal for header actions
  private readonly headerActionsSignal = toSignal(this.modalService.headerActions$);

  constructor(
    private readonly modalService: ModalService,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {
    // Effect to render header actions reactively
    effect(() => {
      // Guard: only run if view is initialized
      if (!this.viewInitialized()) return;

      const config = this.headerActionsSignal();
      // Update config and render (including when config is null to clear)
      this.headerActionsConfig = config ?? null;
      this.renderHeaderActions();
    });
  }

  ngOnInit(): void {
    const trySubscribe = <T>(
      obs: Observable<T> | { subscribe: (fn: (v: T) => void) => Subscription } | undefined,
      next: (value: T) => void
    ) => {
      if (obs && typeof (obs as Observable<T>).subscribe === 'function') {
        const sub = (obs as Observable<T>).subscribe(next);
        this.subscription.add(sub);
      }
    };

    // Access modalService streams with narrow casts to avoid using `any` directly
    const svc = this.modalService as unknown as {
      isOpen$?: Observable<boolean>;
      title$?: Observable<string>;
      isBold$?: Observable<boolean>;
      isCentered$?: Observable<boolean>;
      showSeparator$?: Observable<boolean>;
      showCloseButton$?: Observable<boolean>;
      isRightAligned$?: Observable<boolean>;
    };
    trySubscribe<boolean>(svc.isOpen$, (isOpen) => (this.isOpen = !!isOpen));
    trySubscribe<string>(svc.title$, (title) => (this.title = title || ''));
    trySubscribe<boolean>(svc.isBold$, (bold) => (this.isBold = !!bold));
    trySubscribe<boolean>(svc.isCentered$, (centered) => (this.isCentered = !!centered));
    trySubscribe<boolean>(svc.showSeparator$, (show) => (this.showSeparator = !!show));
    trySubscribe<boolean>(svc.showCloseButton$, (show) => (this.showCloseButton = !!show));
    trySubscribe<boolean>(svc.isRightAligned$, (aligned) => (this.isRightAligned = !!aligned));
  }

  ngAfterViewInit(): void {
    // Signal the view is ready, triggering the effect
    this.viewInitialized.set(true);
  }

  private renderHeaderActions() {
    if (!this.headerActionsContainer) return;
    this.headerActionsContainer.clear();
    if (this.headerActionsConfig && this.headerActionsContainer) {
      const componentRef = this.headerActionsContainer.createComponent(
        this.headerActionsConfig.component
      );
      if (this.headerActionsConfig.props) {
        Object.keys(this.headerActionsConfig.props).forEach((key) =>
          componentRef.setInput(key, this.headerActionsConfig!.props![key])
        );
      }
      if (this.headerActionsConfig.outputs) {
        Object.keys(this.headerActionsConfig.outputs).forEach((key) => {
          const outputEmitter = (componentRef.instance as Record<string, unknown>)[key];
          if (outputEmitter && typeof outputEmitter === 'object' && 'subscribe' in outputEmitter) {
            this.subscription.add(
              (outputEmitter as Observable<unknown>).subscribe(
                this.headerActionsConfig!.outputs![key]
              )
            );
          }
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public loadComponent<T>(
    component: Type<T>,
    props?: Partial<Record<string, unknown>>,
    className?: string
  ) {
    if (className) {
      this.className = className;
    }
    this.content.clear();
    const componentRef = this.content.createComponent(component);
    this.resetScroll();
    if (!props) return;
    Object.keys(props).forEach((keyProp) => componentRef.setInput(keyProp, props[keyProp]));
    return componentRef;
  }

  private resetScroll(): void {
    const scrollTargets = this.elementRef.nativeElement.querySelectorAll(
      '.modal__content, .modal__dialog, [class*="modal__dialog"]'
    );
    setTimeout(() => {
      scrollTargets.forEach((el) => ((el as HTMLElement).scrollTop = 0));
    });
  }

  onClose(): void {
    this.modalService.close();
  }
}
